import {
  Button,
  column,
  Css,
  dateColumn,
  FilterDefs,
  Filters,
  GridColumn,
  HasIdAndName,
  multiFilter,
  RowStyles,
  ScrollableContent,
  simpleDataRows,
  SimpleHeaderAndData,
  usePersistedFilter,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { createExportCsvUrl, createGlobalOptionsFormUrl } from "src/RouteUrls";
import { QueryTable, SearchBox } from "src/components";
import { GlobalOptionsFilter, GlobalOptionStatus } from "src/generated/graphqlTypes";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { PageHeader } from "src/routes/layout/PageHeader";
import {
  GlobalOptionsPageV2_GlobalOptionFragment,
  useGlobalOptionsPageV2MetadataQuery,
  useGlobalOptionsPageV2Query,
} from "src/routes/libraries/global-options-v2/GlobalOptionsPageV2.generated";
import { GlobalOptionsTagColumn } from "src/routes/libraries/global-options/components/GlobalOptionsTagColumn";
import { queryResult } from "src/utils";
import { DateOnly, formatWithShortYear } from "src/utils/dates";
import { openNewTab } from "src/utils/window";

export function GlobalOptionsPageV2() {
  const query = useGlobalOptionsPageV2MetadataQuery();
  useDocumentTitle("Global Options");
  return queryResult(query, {
    data: ({ globalOptionTypes }) => <GlobalOptionsPageV2View globalOptionTypes={globalOptionTypes} />,
  });
}

export type GlobalOptionsPageV2ViewProps = {
  globalOptionTypes: HasIdAndName[];
};

export const globalOptionStatuses = [
  { name: "Available", status: GlobalOptionStatus.Active },
  { name: "Pending Approval", status: GlobalOptionStatus.PendingApproval },
  { name: "Discontinued", status: GlobalOptionStatus.Archived },
];

export function GlobalOptionsPageV2View({ globalOptionTypes }: GlobalOptionsPageV2ViewProps) {
  const [searchFilter, setSearchFilter] = useState<string | undefined>();

  const filterDefs: FilterDefs<GlobalOptionsFilter> = useMemo(() => {
    // Type
    const type = multiFilter({
      label: "Option type",
      options: globalOptionTypes,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    // Status
    const status = multiFilter({
      label: "Status",
      options: globalOptionStatuses,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ status }) => status,
    });
    return { type, status };
  }, [globalOptionTypes]);

  const { filter, setFilter } = usePersistedFilter<GlobalOptionsFilter>({
    storageKey: "globalOptionsFilter",
    filterDefs,
  });

  const query = useGlobalOptionsPageV2Query({
    variables: { filter: { ...filter, isCatalogV2: true, version: [2] } },
  });

  const rowStyles = useMemo(() => createRowStyles(), []);

  return (
    <>
      <PageHeader
        title="Options"
        right={
          <>
            <Button
              label="Export"
              variant="secondary"
              onClick={async () => openNewTab(`${createExportCsvUrl("globalOptions")}`)}
            />
            <Button label="Create option" variant="primary" onClick={createGlobalOptionsFormUrl()} />
          </>
        }
      />
      <div css={Css.my2.df.jcsb.$}>
        <div>
          <Filters<GlobalOptionsFilter> filterDefs={filterDefs} filter={filter} onChange={setFilter} />
        </div>
        <SearchBox onSearch={setSearchFilter} />
      </div>
      <ScrollableContent virtualized>
        <QueryTable
          as="virtual"
          stickyHeader
          filter={searchFilter}
          sorting={{ on: "client" }}
          query={query}
          columns={columns}
          createRows={(data) => simpleDataRows(data?.globalOptions)}
          rowStyles={rowStyles}
          fallbackMessage="No global options found"
          style={{ allWhite: true, bordered: true }}
        />
      </ScrollableContent>
    </>
  );
}

type Row = SimpleHeaderAndData<GlobalOptionsPageV2_GlobalOptionFragment>;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Code", data: ({ code }) => code, w: "110px" }),
  column<Row>({ header: "Internal name", data: ({ name }) => name, mw: "240px" }),
  column<Row>({
    header: "Group",
    // For single option groups, don't needlessly repeat the name
    data: ({ group }) => (group.isSingleOptionGroup ? "" : group.name),
    mw: "100px",
  }),
  column<Row>({ header: "Option type", data: ({ type }) => type.name, w: "160px" }),
  column<Row>({
    header: "Location",
    data: ({ locations }) => locations.map(({ name }) => name).join(", "),
    w: "280px",
  }),
  column<Row>({
    header: "Tag",
    data: (option) => <GlobalOptionsTagColumn option={option} />,
    wrapAction: false,
    clientSideSort: false,
    w: "280px",
  }),
  column<Row>({ header: "Status", data: ({ statusDetail }) => statusDetail.name, w: "100px" }),
  dateColumn<Row>({
    header: "Active date",
    data: ({ activeDate, archivedDate }) => ({
      content: activeDateToArchivedCell(activeDate, archivedDate),
    }),
    w: "160px",
    clientSideSort: false,
  }),
];

function activeDateToArchivedCell(activeDate: DateOnly | null | undefined, archivedDate: DateOnly | null | undefined) {
  if (activeDate && archivedDate) {
    return `${formatWithShortYear(activeDate)} - ${formatWithShortYear(archivedDate)}`;
  }
  return activeDate ? `${formatWithShortYear(activeDate)} - present` : `none`;
}

function createRowStyles(): RowStyles<Row> {
  return { header: {}, data: { rowLink: ({ id }) => createGlobalOptionsFormUrl(id) } };
}
